import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Star, GoogleLogo, FloppyDisk, Info, Copy } from "@phosphor-icons/react";

/**
 * Admin config for the SAV rating flow:
 *  - Google review URL to redirect clients to when they rate ≥ threshold
 *  - Threshold (1–5 stars, default 4)
 *  - Business name displayed on the public rating page
 *  - Master enable switch
 */
export default function GoogleReviewPanel() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/google-review-settings");
      setSettings(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => { load(); }, []);

  const patch = async (updates) => {
    setSaving(true);
    try {
      const r = await api.patch("/admin/google-review-settings", updates);
      setSettings(r.data);
      toast.success("Enregistré");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <div className="text-sm text-white/50">Chargement…</div>;

  const previewUrl = `${window.location.origin}/rate/EXAMPLE-TOKEN`;

  return (
    <div className="space-y-4" data-testid="google-review-panel">
      <div className="rounded-2xl bg-[#005CFF]/8 border border-[#005CFF]/25 p-4 flex gap-3 items-start">
        <Info size={18} weight="duotone" className="text-[#7CB2FF] shrink-0 mt-0.5" />
        <div className="text-sm text-white/80 leading-relaxed">
          Chaque email/PDF de rapport SAV envoyé au client inclut un lien
          <b> « ⭐ Noter cette intervention »</b>. Si le client met une note ≥ <b>{settings.redirect_threshold}</b>,
          {" "}il est redirigé vers votre <b>page Google</b> pour laisser un avis public.
          <div className="text-xs text-white/50 mt-1">
            Cible : ~30 % d&apos;avis Google en plus sur les SAV résolus.
          </div>
        </div>
      </div>

      {/* Master switch */}
      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 flex items-center justify-between">
        <div>
          <div className="font-medium text-sm">Activer le flow de notation SAV</div>
          <div className="text-xs text-white/50">Lien intégré dans les emails et le pied de PDF</div>
        </div>
        <Switch
          checked={settings.enabled !== false}
          onCheckedChange={(v) => patch({ enabled: v })}
          data-testid="google-review-enabled"
        />
      </div>

      {/* Google URL */}
      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 space-y-3">
        <label className="text-xs uppercase tracking-widest text-white/60 flex items-center gap-1.5">
          <GoogleLogo size={13} weight="bold" /> URL Google Reviews
        </label>
        <Input
          type="url"
          placeholder="https://g.page/r/CxXxXxXxXxX/review"
          value={settings.google_review_url || ""}
          onChange={(e) => setSettings((s) => ({ ...s, google_review_url: e.target.value }))}
          className="bg-white/5 border-white/10"
          data-testid="google-review-url"
        />
        <div className="text-[11px] text-white/40 leading-relaxed">
          Ouvrez votre <a href="https://business.google.com/" target="_blank" rel="noreferrer" className="text-[#7CB2FF] hover:underline">profil Google Business</a>
          {" "}→ &laquo; Demander des avis &raquo; → copiez le lien court (souvent en <code>g.page/r/…/review</code>).
        </div>
      </div>

      {/* Business name */}
      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 space-y-3">
        <label className="text-xs uppercase tracking-widest text-white/60">Nom affiché au client</label>
        <Input
          type="text"
          placeholder="BKPRO Électricité"
          value={settings.business_name || ""}
          onChange={(e) => setSettings((s) => ({ ...s, business_name: e.target.value }))}
          className="bg-white/5 border-white/10"
          data-testid="google-review-business-name"
        />
      </div>

      {/* Threshold */}
      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 space-y-3">
        <label className="text-xs uppercase tracking-widest text-white/60 flex items-center gap-1.5">
          <Star size={13} weight="fill" className="text-amber-400" /> Seuil de redirection Google
        </label>
        <div className="flex items-center gap-2">
          {[3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setSettings((s) => ({ ...s, redirect_threshold: n }))}
              className={`flex items-center gap-1 h-10 px-4 rounded-xl border transition-colors ${settings.redirect_threshold === n ? "bg-[#005CFF] border-[#005CFF] text-white" : "bg-white/5 border-white/10 text-white/60 hover:border-white/25"}`}
              data-testid={`google-review-threshold-${n}`}
            >
              {Array.from({ length: n }).map((_, i) => (
                <Star key={`star-${n}-${i}`} size={13} weight="fill" />
              ))}
              <span className="ml-1 text-xs">≥ {n}</span>
            </button>
          ))}
        </div>
        <div className="text-[11px] text-white/40">
          Les notes en-dessous du seuil restent internes — pas de redirection Google.
        </div>
      </div>

      {/* Save button */}
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => { navigator.clipboard.writeText(previewUrl); toast.success("URL exemple copiée"); }}
          className="inline-flex items-center gap-1.5 text-xs text-white/50 hover:text-white/80"
          data-testid="google-review-preview-copy"
        >
          <Copy size={12} /> Exemple d&apos;URL client : <code className="text-white/70">/rate/{`{token}`}</code>
        </button>
        <Button
          onClick={() => patch({
            google_review_url: settings.google_review_url || null,
            business_name: settings.business_name || null,
            redirect_threshold: settings.redirect_threshold || 4,
            enabled: settings.enabled !== false,
          })}
          disabled={saving}
          className="bg-emerald-500 hover:bg-emerald-600 text-black"
          data-testid="google-review-save"
        >
          <FloppyDisk size={13} weight="fill" /> Enregistrer
        </Button>
      </div>
    </div>
  );
}
